import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Grid, TextField, MenuItem, Button, Chip, Checkbox, FormControlLabel, Alert, Typography,
  CircularProgress, Divider
} from "@mui/material";
import { Refresh, NetworkCheck, Save, Sync } from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  aiCallApi, useAICallOverview, useAICallEvents, formatNumber, dhaka, reasonText
} from "./useAICalling";
import { AICallingPage, SectionCard, Loading, ErrorState, SipStateChip, KeyValue } from "./AICallingComponents";

const HOST = /^[a-zA-Z0-9.-]+$/;
const FIELDS = ["host", "port", "username", "authUsername", "callerId", "outboundProxy", "maxChannels", "registerExpires"];

const toForm = (sip = {}) => ({
  host: sip.host || "",
  port: String(sip.port ?? 5060),
  username: sip.username || "",
  authUsername: sip.authUsername || "",
  password: "",
  clearPassword: false,
  callerId: sip.callerId || "",
  outboundProxy: sip.outboundProxy || "",
  maxChannels: String(sip.maxChannels ?? 5),
  registerExpires: String(sip.registerExpires ?? 300),
});

const whole = (v, min, max) => v !== "" && Number.isInteger(Number(v)) && Number(v) >= min && Number(v) <= max;

/** Field checks (same limits as the backend). forTest: host and extension are required. */
const validate = (f, forTest) => {
  const host = f.host.trim();
  if (forTest && !host) return "Enter the SIP server";
  if (host && !HOST.test(host)) return "SIP server must be a host name or IP address";
  if (!whole(f.port, 1, 65535)) return "Port must be 1–65535";
  if (forTest && !f.username.trim()) return "Enter the extension / username";
  if (f.username.trim().length > 64) return "Extension / username: at most 64 characters";
  if (f.authUsername.trim().length > 64) return "Auth username: at most 64 characters";
  if (f.callerId.trim().length > 32) return "Caller ID: at most 32 characters";
  if (f.outboundProxy.trim().length > 128) return "Outbound proxy: at most 128 characters";
  if (f.password.length > 128) return "Password too long";
  if (!whole(f.maxChannels, 1, 100)) return "Channels must be 1–100";
  if (!whole(f.registerExpires, 60, 3600)) return "Registration expiry must be 60–3600 seconds";
  return null;
};

const baseSip = (f) => ({
  host: f.host.trim(),
  port: Number(f.port),
  transport: "udp",
  username: f.username.trim(),
  authUsername: f.authUsername.trim(),
  callerId: f.callerId.trim(),
  outboundProxy: f.outboundProxy.trim(),
  maxChannels: Number(f.maxChannels),
  registerExpires: Number(f.registerExpires),
});

const AICallingSip = () => {
  const { overview, loading, error, fetch } = useAICallOverview();
  const [form, setForm] = useState(toForm());
  const [applyNow, setApplyNow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [rereg, setRereg] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [liveSip, setLiveSip] = useState(null);

  useEffect(() => {
    fetch();
  }, [fetch]);

  // refill the form only when the saved line changes (a plain refresh keeps what was typed)
  const savedSip = overview?.settings?.sip;
  const savedKey = savedSip ? JSON.stringify(savedSip) : "";
  const lastSavedKey = useRef("");
  useEffect(() => {
    if (!savedSip || savedKey === lastSavedKey.current) return;
    lastSavedKey.current = savedKey;
    setForm(toForm(savedSip));
  }, [savedSip, savedKey]);
  useEffect(() => {
    setLiveSip(null);
  }, [overview]);

  const onEvent = useCallback((type, data) => {
    if (type === "sip:status") setLiveSip(data);
  }, []);
  useAICallEvents(onEvent);

  const set = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value, ...(field === "clearPassword" && value ? { password: "" } : {}) }));
    setTestResult(null);
  };

  const testRegistration = async () => {
    const err = validate(form, true);
    if (err) return toast.error(err);
    const typed = form.password && !form.clearPassword;
    if (!typed && (!savedSip?.passwordSet || form.clearPassword)) return toast.error("Enter the password to test");
    setTesting(true);
    setTestResult(null);
    const res = await aiCallApi.testSip({ ...baseSip(form), ...(typed ? { password: form.password } : {}) });
    setTesting(false);
    if (!res.ok) return setTestResult({ ok: false, error: res.error });
    setTestResult(res.data?.ok ? { ok: true } : { ok: false, error: reasonText(res.data?.error) || "Registration failed", sipCode: res.data?.sipCode });
  };

  const save = async () => {
    const err = validate(form, false);
    if (err) return toast.error(err);
    const sip = {
      ...baseSip(form),
      ...(form.password && !form.clearPassword ? { password: form.password } : {}),
      ...(form.clearPassword ? { clearPassword: true } : {}),
    };
    setSaving(true);
    const res = await aiCallApi.saveSip(sip, applyNow);
    setSaving(false);
    if (!res.ok) return toast.error(res.error);
    const r = res.body?.reregister;
    if (r && r.ok === false) toast.warning(`Saved, but re-registering didn't start: ${reasonText(r.error)}`);
    else if (r?.pending) toast.info("Saved. It re-registers when the calls in progress finish.");
    else toast.success(res.message || "SIP line saved");
    setForm((f) => ({ ...f, password: "", clearPassword: false }));
    setApplyNow(false);
    fetch({ quiet: true });
  };

  const reregister = async () => {
    setRereg(true);
    const res = await aiCallApi.reregister(applyNow);
    setRereg(false);
    if (!res.ok) return toast.error(res.error);
    if (res.data?.pending) toast.info("It re-registers when the calls in progress finish.");
    else toast.success("Re-registering");
    fetch({ quiet: true });
  };

  const actions = <Button variant="outlined" startIcon={<Refresh />} onClick={() => fetch()} disabled={loading}>Refresh</Button>;
  const page = (children) => (
    <AICallingPage title="SIP line" subtitle="The phone line the AI agent registers and calls from" actions={actions}>
      {children}
    </AICallingPage>
  );

  if (loading && !overview) return page(<Loading />);
  if (!overview) return page(<ErrorState error={error || "Could not load the SIP line"} onRetry={() => fetch()} />);
  if (!overview.can?.sipLine) return page(<Alert severity="info">Only Super Admin can view or change the SIP line.</Alert>);

  const engine = overview.engine || {};
  const engineOk = !!engine.ok;
  const sip = { ...(engine.sip || {}), ...(liveSip || {}) };
  const channels = engine.channels || {};
  const pending = engineOk && sip.version !== null && sip.version !== undefined && sip.version !== savedSip?.version;
  const dirty = FIELDS.some((k) => String(form[k]).trim() !== String(toForm(savedSip)[k]).trim()) || !!form.password || form.clearPassword;
  const busy = saving || testing || rereg;

  return page(
    <>
      {!overview.secretKeySet && (
        <Alert severity="error" sx={{ mb: 2 }}>
          AICALL_SECRET_KEY isn't set on the server, so a SIP password can't be saved.
        </Alert>
      )}
      {!engineOk && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {engine.error === "engine_offline"
            ? "The calling process isn't running on the server. You can save the line; it registers when the process starts. Testing needs the process."
            : `Can't reach the calling process: ${reasonText(engine.error) || "unknown error"}`}
        </Alert>
      )}

      <SectionCard
        title="Status"
        action={
          <Button size="small" startIcon={rereg ? <CircularProgress size={14} /> : <Sync />} onClick={reregister} disabled={busy || !engineOk}>
            Re-register now
          </Button>
        }
      >
        <Grid container spacing={2}>
          <KeyValue label="State">{engineOk ? <SipStateChip state={sip.state} /> : <Chip size="small" color="error" label="Calling process offline" />}</KeyValue>
          <KeyValue label="Last registered">{sip.lastRegisteredAt ? dhaka(sip.lastRegisteredAt) : ""}</KeyValue>
          <KeyValue label="Channels in use">
            {engineOk ? (
              <>
                {`${formatNumber(channels.inUse)} / ${formatNumber(channels.max)}`}
                {savedSip?.maxChannels > channels.max && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Line has {formatNumber(savedSip.maxChannels)}; "Calls at once" in Settings allows {formatNumber(channels.max)}.
                  </Typography>
                )}
              </>
            ) : ""}
          </KeyValue>
          <KeyValue label="Last error" xs={12} sm={12} md={12}>
            {sip.lastError ? <Typography variant="body2" color="error">{sip.lastError}</Typography> : ""}
          </KeyValue>
        </Grid>
        {pending && (
          <Alert severity="info" sx={{ mt: 2 }}>Saved changes are waiting to be applied (when the calls in progress finish).</Alert>
        )}
      </SectionCard>

      <SectionCard
        title="Line settings"
        subtitle="BibahoBangla's own extension on the PBX, used only for AI calls"
        action={dirty ? <Chip size="small" color="warning" variant="outlined" label="Unsaved changes" /> : undefined}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField label="SIP server" fullWidth size="small" value={form.host} onChange={set("host")}
              helperText="Host name or IP address of the PBX" inputProps={{ autoComplete: "off", spellCheck: false }} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField label="Port" type="number" fullWidth size="small" value={form.port} onChange={set("port")} inputProps={{ min: 1, max: 65535 }} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField select label="Transport" fullWidth size="small" value="udp" disabled helperText="UDP only">
              <MenuItem value="udp">UDP</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField label="Extension / username" fullWidth size="small" value={form.username} onChange={set("username")}
              inputProps={{ maxLength: 64, autoComplete: "off", spellCheck: false }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Auth username" fullWidth size="small" value={form.authUsername} onChange={set("authUsername")}
              helperText="Leave empty to use the extension" inputProps={{ maxLength: 64, autoComplete: "off", spellCheck: false }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography variant="body2">Saved password:</Typography>
              <Chip size="small" color={savedSip?.passwordSet ? "success" : "default"} label={savedSip?.passwordSet ? "Set" : "Not set"} />
            </Box>
            <TextField
              label={savedSip?.passwordSet ? "New password" : "Password"}
              type="password"
              fullWidth
              size="small"
              value={form.password}
              onChange={set("password")}
              disabled={form.clearPassword}
              helperText={savedSip?.passwordSet ? "Leave empty to keep the saved password" : "Stored encrypted; never shown again"}
              inputProps={{ maxLength: 128, autoComplete: "new-password" }}
            />
            {savedSip?.passwordSet && (
              <FormControlLabel
                control={<Checkbox checked={form.clearPassword} onChange={set("clearPassword")} />}
                label="Remove saved password"
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Caller ID" fullWidth size="small" value={form.callerId} onChange={set("callerId")} sx={{ mt: { sm: 4.5 } }}
              helperText="Number the member sees; empty = the extension" inputProps={{ maxLength: 32, autoComplete: "off" }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField label="Outbound proxy (optional)" fullWidth size="small" value={form.outboundProxy} onChange={set("outboundProxy")}
              helperText="host[:port], if the PBX needs one" inputProps={{ maxLength: 128, autoComplete: "off", spellCheck: false }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Channels" type="number" fullWidth size="small" value={form.maxChannels} onChange={set("maxChannels")}
              helperText="Calls the line carries at once (1–100)" inputProps={{ min: 1, max: 100 }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Registration expiry (s)" type="number" fullWidth size="small" value={form.registerExpires} onChange={set("registerExpires")}
              helperText="60–3600" inputProps={{ min: 60, max: 3600 }} />
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          If you lower the channels below "Calls at once" in Settings, that is lowered too.
        </Typography>

        {(testing || testResult) && (
          <Box sx={{ mt: 2 }}>
            {testing ? (
              <Alert severity="info" icon={<CircularProgress size={18} />}>Testing registration… this can take up to 25 seconds.</Alert>
            ) : testResult.ok ? (
              <Alert severity="success">Registration worked with these settings. Nothing was saved.</Alert>
            ) : (
              <Alert severity="error">
                Registration failed: {testResult.error}{testResult.sipCode ? ` (SIP ${testResult.sipCode})` : ""}
              </Alert>
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <FormControlLabel
            control={<Checkbox checked={applyNow} onChange={(e) => setApplyNow(e.target.checked)} />}
            label="Apply now even if calls are in progress"
          />
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="outlined" startIcon={testing ? <CircularProgress size={16} /> : <NetworkCheck />} onClick={testRegistration} disabled={busy}>
              Test registration
            </Button>
            <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />} onClick={save} disabled={busy}>
              Save & re-register
            </Button>
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Test registration uses the fields above (and the saved password if you leave it empty). Without "Apply now", the new
          settings are used once the calls in progress finish.
        </Typography>
        {savedSip?.version !== undefined && (
          <Typography variant="caption" color="text.secondary" display="block">
            Line version {savedSip.version}{overview.settings?.updatedAt ? ` · settings last changed ${dhaka(overview.settings.updatedAt)}${overview.settings.updatedByName ? ` by ${overview.settings.updatedByName}` : ""}` : ""}
          </Typography>
        )}
      </SectionCard>
    </>
  );
};

export default AICallingSip;
